import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Typography from "components/basic/typography";

interface QuotePartiesProps {
  customerName: string;
  customerAddress: string;
  customerPhone: string;
  customerEmail: string;
  estimateNumber: string;
  date: string;
}

/**
 * The "bill to" row of the estimate: customer contact details on the left and
 * the estimate number / date on the right.
 */
const QuoteParties: React.FC<QuotePartiesProps> = ({
  customerName,
  customerAddress,
  customerPhone,
  customerEmail,
  estimateNumber,
  date,
}) => {
  const { t } = useTranslation("estimator");
  const theme = useTheme();

  const containerSx = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: "flex-start",
    gap: theme.spacing(2),
  };

  // Pushed right to sit opposite the customer block; once stacked there is
  // nothing to sit opposite, so it returns to the reading edge.
  const metaRowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: { xs: "flex-start", sm: "flex-end" },
    gap: theme.spacing(1),
  };

  const metaLabelStyle: React.CSSProperties = {
    fontSize: "1em",
  };

  const renderMetaRow = (label: string, value: string) => (
    <Box sx={metaRowSx}>
      <Typography variant="body1" style={metaLabelStyle}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );

  return (
    <Box sx={containerSx}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2">{customerName}</Typography>
        <Typography variant="body2">{customerAddress}</Typography>
        <Typography variant="body2">{customerPhone}</Typography>
        <Typography variant="body2">{customerEmail}</Typography>
      </Box>

      <Box>
        {renderMetaRow(t("quote.estimate_no"), estimateNumber)}
        {renderMetaRow(t("quote.date"), date)}
      </Box>
    </Box>
  );
};

export default QuoteParties;
